import React, { useContext, useState, useEffect } from "react";
import { TranslatorContext } from "../../context/Translator";
import { useNavigate, Link } from "react-router-dom";
import { Row, Col, Card, Button, Form, Alert, Spinner } from "react-bootstrap";
import axios from "axios";
import PageLayout from "../../layouts/PageLayout";

const COUNTRY_LIST = [
    "United States", "United Kingdom", "Canada", "Australia", "Germany", "France",
    "United Arab Emirates", "Saudi Arabia", "Pakistan", "India", "Afghanistan",
    "Albania", "Algeria", "Andorra", "Angola", "Argentina", "Armenia", "Austria",
    "Azerbaijan", "Bahrain", "Bangladesh", "Belgium", "Brazil", "Bulgaria",
    "China", "Colombia", "Croatia", "Cyprus", "Czech Republic", "Denmark",
    "Egypt", "Estonia", "Finland", "Georgia", "Greece", "Hong Kong", "Hungary",
    "Iceland", "Indonesia", "Iraq", "Ireland", "Italy", "Japan", "Jordan",
    "Kuwait", "Latvia", "Lebanon", "Lithuania", "Luxembourg", "Malaysia",
    "Mexico", "Morocco", "Netherlands", "New Zealand", "Nigeria", "Norway",
    "Oman", "Philippines", "Poland", "Portugal", "Qatar", "Romania", "Russia",
    "Singapore", "South Africa", "South Korea", "Spain", "Sri Lanka", "Sweden",
    "Switzerland", "Thailand", "Turkey", "Ukraine", "Vietnam"
];

export default function PunchOrderPage() {
    const { t } = useContext(TranslatorContext);
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [fetchingInitial, setFetchingInitial] = useState(true);
    const [marketplaces, setMarketplaces] = useState([]);
    const [error, setError] = useState(null);

    const [formData, setFormData] = useState({
        marketplace_id: "",
        product_code: "",
        title: "",
        name: "",
        email: "",
        phone: "",
        country: "",
        city: "",
        address: "",
        vat_id: "",
        payment_date: "",
        payment_message: "",
        unit_price: "",
    });

    const [image1, setImage1] = useState(null);
    const [image2, setImage2] = useState(null);
    const [preview1, setPreview1] = useState(null);
    const [preview2, setPreview2] = useState(null);

    useEffect(() => {
        const fetchInitialData = async () => {
            try {
                const response = await axios.get(
                    `${process.env.REACT_APP_API_URL}/market-placer/initial-data`,
                    {
                        headers: {
                            Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                        },
                    }
                );
                if (response.data?.success && response.data?.data?.market_places) {
                    setMarketplaces(response.data.data.market_places);
                }
            } catch (err) {
                console.error("Error fetching initial data:", err);
            } finally {
                setFetchingInitial(false);
            }
        };

        fetchInitialData();
    }, []);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleImage1Change = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImage1(file);
            setPreview1(URL.createObjectURL(file));
        }
    };

    const handleImage2Change = (e) => {
        const file = e.target.files[0];
        if (file) {
            setImage2(file);
            setPreview2(URL.createObjectURL(file));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        const data = new FormData();
        Object.keys(formData).forEach((key) => {
            if (formData[key]) data.append(key, formData[key]);
        });

        if (image1) data.append("image_1", image1);
        if (image2) data.append("image_2", image2);

        try {
            const response = await axios.post(
                `${process.env.REACT_APP_API_URL}/market-placer/product/punch-my-order`,
                data,
                {
                    headers: {
                        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );

            if (response.data?.success) {
                navigate("/market-placer/my-order");
            } else {
                setError(response.data?.message || t("Failed to punch order."));
            }
        } catch (err) {
            setError(err.response?.data?.message || err.response?.data?.error || t("Failed to punch order."));
        } finally {
            setLoading(false);
        }
    };

    return (
        <PageLayout>
            <Row>
                <Col xl={12}>
                    <Card className="mc-card mb-4 border-0 shadow-sm">
                        <Card.Body className="mc-breadcrumb d-flex justify-content-between align-items-center py-3">
                            <div>
                                <h3 className="mc-breadcrumb-title mb-1 fw-bold">{t("Punch Unassigned Order")}</h3>
                                <ul className="mc-breadcrumb-list d-flex gap-2 p-0 m-0 list-unstyled small text-muted">
                                    <li><Link to="#" className="text-decoration-none text-muted">{t("Home")}</Link></li>
                                    <li>/</li>
                                    <li><Link to="/market-placer/my-order" className="text-decoration-none text-muted">{t("My Orders")}</Link></li>
                                    <li>/</li>
                                    <li className="text-primary fw-semibold">{t("Punch Order")}</li>
                                </ul>
                            </div>
                            <Button variant="secondary" size="sm" onClick={() => navigate(-1)}>
                                {t("Back")}
                            </Button>
                        </Card.Body>
                    </Card>

                    <Card className="border-0 shadow-sm mb-4">
                        <Card.Body className="p-4">
                            {error && <Alert variant="danger">{error}</Alert>}

                            <Form onSubmit={handleSubmit}>
                                <h5 className="fw-bold mb-3">{t("Product & Order Info")}</h5>
                                <Row className="g-3 mb-4">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("Marketplace")} *</Form.Label>
                                            <Form.Select
                                                name="marketplace_id"
                                                required
                                                value={formData.marketplace_id}
                                                onChange={handleChange}
                                                disabled={fetchingInitial}
                                            >
                                                <option value="">{t("Select Marketplace")}</option>
                                                {marketplaces.map((mp) => (
                                                    <option key={mp.id} value={mp.id}>
                                                        {mp.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("Product Code")} *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="product_code"
                                                placeholder={t("e.g. PRD-12345")}
                                                required
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("Sell Price ($)")} *</Form.Label>
                                            <Form.Control
                                                type="number"
                                                step="0.01"
                                                name="unit_price"
                                                placeholder={t("e.g. 299.99")}
                                                required
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>{t("Title")} *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="title"
                                                placeholder={t("Enter product full title")}
                                                required
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <h5 className="fw-bold mb-3">{t("Buyer Info")}</h5>
                                <Row className="g-3 mb-4">
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("Buyer Name")} *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="name"
                                                placeholder={t("Enter buyer name")}
                                                required
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("Buyer Email")}</Form.Label>
                                            <Form.Control
                                                type="email"
                                                name="email"
                                                placeholder={t("buyer@example.com")}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("Phone")} *</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="phone"
                                                placeholder={t("+1 234 567 890")}
                                                required
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("Country")} *</Form.Label>
                                            <Form.Select
                                                name="country"
                                                required
                                                value={formData.country}
                                                onChange={handleChange}
                                            >
                                                <option value="">{t("Select Country")}</option>
                                                {COUNTRY_LIST.map((country) => (
                                                    <option key={country} value={country}>
                                                        {country}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("City")}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="city"
                                                placeholder={t("Enter city name")}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group>
                                            <Form.Label>{t("VAT ID")}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="vat_id"
                                                placeholder={t("Enter VAT or tax ID")}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={12}>
                                        <Form.Group>
                                            <Form.Label>{t("Address")} *</Form.Label>
                                            <Form.Control
                                                as="textarea"
                                                rows={2}
                                                name="address"
                                                placeholder={t("Enter full shipping address")}
                                                required
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                </Row>

                                <h5 className="fw-bold mb-3">{t("Payment & Document Attachments")}</h5>
                                <Row className="g-3 mb-4">
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>{t("Payment Date")}</Form.Label>
                                            <Form.Control
                                                type="date"
                                                name="payment_date"
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>{t("Payment Message")}</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="payment_message"
                                                placeholder={t("e.g. Paid via PayPal")}
                                                onChange={handleChange}
                                            />
                                        </Form.Group>
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>{t("Address Picture")} *</Form.Label>
                                            <Form.Control
                                                type="file"
                                                accept="image/*"
                                                required
                                                onChange={handleImage1Change}
                                            />
                                        </Form.Group>
                                        {preview1 && (
                                            <div className="mt-2 text-center p-2 border rounded bg-light">
                                                <img
                                                    src={preview1}
                                                    alt="Address Preview"
                                                    style={{ maxHeight: "150px", objectFit: "contain", maxWidth: "100%" }}
                                                    className="rounded"
                                                />
                                            </div>
                                        )}
                                    </Col>
                                    <Col md={6}>
                                        <Form.Group>
                                            <Form.Label>{t("Earning Picture")} *</Form.Label>
                                            <Form.Control
                                                type="file"
                                                accept="image/*"
                                                required
                                                onChange={handleImage2Change}
                                            />
                                        </Form.Group>
                                        {preview2 && (
                                            <div className="mt-2 text-center p-2 border rounded bg-light">
                                                <img
                                                    src={preview2}
                                                    alt="Earning Preview"
                                                    style={{ maxHeight: "150px", objectFit: "contain", maxWidth: "100%" }}
                                                    className="rounded"
                                                />
                                            </div>
                                        )}
                                    </Col>
                                </Row>

                                <div className="d-flex justify-content-end gap-2">
                                    <Button variant="secondary" onClick={() => navigate(-1)}>
                                        {t("Cancel")}
                                    </Button>
                                    <Button variant="primary" type="submit" disabled={loading}>
                                        {loading ? <Spinner animation="border" size="sm" /> : t("Punch Order")}
                                    </Button>
                                </div>
                            </Form>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </PageLayout>
    );
}